// @ts-nocheck
import React from 'react';
import PropTypes from 'react-proptypes';
import {
  Element, FormValue, User, Role, Record,
} from '@fulcrumapp/fulcrum-core';
import cx from 'classnames';
import Immutable from 'immutable';
import ElementLabel from './element-label';
import ElementValueLabel from './element-value-label';
import ElementRequiredIndicator from './element-required-indicator';
import ElementDescriptionButton from './element-description-button';
import { shallowEqualImmutable, diff } from '../../../utils/scu';
import Collapse from '../../../shared/collapse';
import THEME from '../../../shared/theme';
import { cssInJS } from '../../../utils/styles';

export default class ElementView extends React.Component {
  static propTypes = {
    disabled: PropTypes.bool.isRequired,
    editing: PropTypes.bool.isRequired,
    element: PropTypes.instanceOf(Element).isRequired,
    required: PropTypes.bool.isRequired,
    value: PropTypes.instanceOf(FormValue).isRequired,
    visible: PropTypes.bool.isRequired,
    record: PropTypes.instanceOf(Record).isRequired,
  };

  shouldComponentUpdate(nextProps, nextState) {
    // Implement a pure render check for values. The special case here is that the `value`
    // property is a deeply mutable object. We use the JSON representation as the comparison
    // value to determine if a field's value has changed. The code below is checking for either:
    //
    //  1. Did any of the props or state change EXCEPT `value`
    //  2. Did `value` change, by comparing the JSON represenation
    //  3. Did any of the override attributes change on the *element* (SETHIDDEN, SETLABEL, etc)

    /* eslint-disable no-use-before-define, no-unused-vars */
    const { value: nextValue, ...next } = nextProps;
    const { value: prevValue, ...prev } = this.props;
    /* eslint-enable no-use-before-define, no-unused-vars */

    let shouldUpdate = !shallowEqualImmutable(prev, next) || !shallowEqualImmutable(this.state, nextState);

    const valuesJSON = nextValue && Immutable.fromJS(nextValue.toJSON());
    const elementsJSON = this.props.element && Immutable.fromJS(this.props.element.overrideValues);
    const formJSON = this.props.record.form && Immutable.fromJS(this.props.record.form.overrideValues);

    shouldUpdate = shouldUpdate || diff(this._lastJSONValue, valuesJSON);
    shouldUpdate = shouldUpdate || diff(this._lastJSONElement, elementsJSON);
    shouldUpdate = shouldUpdate || diff(this._lastJSONForm, formJSON);

    this._lastJSONValue = valuesJSON;
    this._lastJSONElement = elementsJSON;
    this._lastJSONForm = formJSON;

    return shouldUpdate;
  }

  render() {
    const { styles } = ElementView;

    const classes = cx('element', this.props.element.type,
      `element-key-${this.props.element.key}`,
      `element-data-name-${this.props.element.dataName}`);

    const cssStyles = css(styles.container,
      this.props.element.elements == null && styles.border,
      this.classNames,
      this.props.editing && styles.editing,
      this.props.required && styles.required,
      this.props.disabled && styles.disabled);

    const { element } = this.props;

    return (
      <Collapse open={this.props.visible}>
        <div className={cx(classes, cssStyles)} data-testid={`element-${element.dataName}`}>
          <div className={css(styles.content)}>
            {this.renderContent()}
          </div>
        </div>
      </Collapse>
    );
  }

  renderContent() {
    const views = [];

    const { styles } = ElementView;

    views.push((
      <ElementLabel
        key="label"
        element={this.props.element}
        className={css(styles.label)}
      />
    ));

    views.push((
      <ElementRequiredIndicator
        key="required"
        element={this.props.element}
        required={this.props.required}
      />
    ));

    const descriptionStyle = this.props.element.description ? styles.withDescription
      : styles.withoutDescription;

    views.push((
      <div
        key="editable"
        className={css(styles.editableContent, descriptionStyle)}
      >
        {this.renderEditableContent()}
      </div>
    ));

    views.push((
      <ElementDescriptionButton
        key="info"
        element={this.props.element}
      />
    ));

    return views;
  }

  renderEditableContent() {
    if (this.props.editing) {
      return this.renderEditable();
    } if (this.renderViewOnly) {
      return this.renderViewOnly();
    }

    return this.renderDefaultViewOnly();
  }

  renderDefaultViewOnly() {
    return <ElementValueLabel value={this.props.value} />;
  }

  get classNames() {
    // empty
  }

  get canEdit() {
    return !this.props.disabled;
  }
}

const styles = cssInJS({
  container: {
    width: '100%',
    position: 'relative',
    padding: 0,
    clear: 'both',
    fontSize: 13,
    backgroundColor: '#fff',

    ':last-child': {
      // borderBottom: 'none'
    },
  },

  border: {
    borderBottom: THEME.fieldBorderLight,
  },

  editableContent: {
    display: 'inline-block',
    paddingRight: 12,

    '@phone': {
      display: 'block',
      width: '100%',
      paddingLeft: 8,
      paddingRight: 8,
      paddingBottom: 8,
      marginTop: -8,
    },
  },

  withoutDescription: {
    width: '58%',

    '@phone': {
      width: '100%',
    },
  },

  withDescription: {
    width: '55%',

    '@phone': {
      width: '100%',
    },
  },

  content: {
    display: 'flex',
    flexDirection: 'row',

    '@phone': {
      display: 'block',
    },
  },
});

ElementView.styles = styles;
